import { put, select } from 'redux-saga/effects';
import { preloaderActionTypes } from './preloader.actions';

import { preloadResource } from 'utils/preloader';

export function* preloadVideo(action) {
    try {
        const state = yield select((state) => state.preloader);

        // Dedupe: if this videoId is already being loaded or already done,
        // ignore the duplicate dispatch. Prevents wasted XHRs from React
        // StrictMode double-mount in dev, and protects against any future
        // caller that might re-dispatch the same video. A failed entry
        // (loading=false, ready=false) is allowed to fall through so an
        // explicit retry still works.
        const existingEntry = state.videos[action.payload.videoId];
        if (existingEntry && (existingEntry.loading || existingEntry.ready)) {
            return;
        }

        if (state.currentlyLoading) {
            const resource = {
                resourceType: 'video',
                resourceId: action.payload.videoId,
                resourceUrl: action.payload.videoUrl,
            };

            yield put({
                type: preloaderActionTypes.PRELOAD_VIDEO_IN_QUEUE,
                payload: { resource },
            });
        } else {
            yield put({
                type: preloaderActionTypes.PRELOAD_VIDEO_FETCHING,
                payload: { videoId: action.payload.videoId, videoUrl: action.payload.videoUrl },
            });
        }
    } catch (error) {
        yield put({
            type: preloaderActionTypes.PRELOAD_VIDEO_FAILURE,
            payload: { videoId: action.payload.videoId, error },
        });
    }
}

// 120s is the upper bound for the video preload XHR. Real videos are 50-60 MB,
// so this covers users down to ~4 Mbps — basically anyone on broadband or
// reasonable mobile data. The preload runs in the background while the user
// goes through earlier modules, so for most users the wait is invisible; for
// borderline-slow connections we show a "loading is slow, please wait" hint
// after 10s. Only truly poor connections (< 4 Mbps) hit the 120s timeout
// and get blocked, since recording while a video stutters would invalidate
// the assessment data.
const VIDEO_PRELOAD_TIMEOUT_MS = 120000;

export function* fetchingVideo(action) {
    try {
        const response = yield preloadResource(
            action.payload.videoUrl,
            VIDEO_PRELOAD_TIMEOUT_MS,
        );

        // Use the blob URL on every platform — preloadResource re-wraps
        // the bytes with an explicit video/mp4 MIME type which iOS 18.7+
        // requires. The original streaming URL is kept as a fallback so
        // VideoComponent can swap on src-load failure (older Safari can
        // still reject blob URLs, and we'd rather degrade to streaming
        // than show the user a black screen with MEDIA_ERR_SRC_NOT_SUPPORTED).
        const videoUrl = URL.createObjectURL(response);
        const fallbackUrl = action.payload.videoUrl;
        yield put({
            type: preloaderActionTypes.PRELOAD_VIDEO_DONE,
            payload: { videoId: action.payload.videoId, videoUrl, fallbackUrl },
        });
    } catch (error) {
        yield put({
            type: preloaderActionTypes.PRELOAD_VIDEO_FAILURE,
            payload: { videoId: action.payload.videoId, error },
        });
    }

    // Drain the queue regardless of whether THIS preload succeeded or failed
    // — otherwise a single failed video would leave any queued videos stuck
    // at loading:true forever.
    const state = yield select((state) => state.preloader);
    if (state.queue.length > 0) {
        const nextVideoToPreload = state.queue[0];
        yield put({
            type: preloaderActionTypes.PRELOAD_VIDEO,
            payload: {
                videoId: nextVideoToPreload.resourceId,
                videoUrl: nextVideoToPreload.resourceUrl,
                loadedFromQueue: true,
            },
        });
    }
}
